import React from 'react';
import { TrendingUp, Users, Clock, Star } from 'lucide-react';

const scoreMetrics = [
  { label: "Entrega de Tarefas", value: 96, color: "#2563eb", weight: "35%" },
  { label: "Pontualidade", value: 91, color: "#059669", weight: "30%" },
  { label: "Engajamento em Projetos", value: 88, color: "#7c3aed", weight: "20%" },
  { label: "Aderência à Rotina", value: 97, color: "#0891b2", weight: "15%" },
];

const teamRows = [
  { name: "Ana Paula Ribeiro", role: "Gestora de Operações", score: 97, delta: "+2.1", status: "elite" },
  { name: "Carlos Mendonça", role: "Coord. de Processos", score: 91, delta: "+0.8", status: "high" },
  { name: "Fernanda Costa", role: "Analista Sênior", score: 84, delta: "-1.2", status: "mid" },
  { name: "Rafael Torres", role: "Coord. de Qualidade", score: 72, delta: "-3.5", status: "low" },
];

const statusLabel: Record<string, { label: string; cls: string }> = {
  elite: { label: "Elite", cls: "bg-blue-50 text-blue-700 border-blue-100" },
  high: { label: "Alto", cls: "bg-emerald-50 text-emerald-700 border-emerald-100" },
  mid: { label: "Médio", cls: "bg-amber-50 text-amber-700 border-amber-100" },
  low: { label: "Em Atenção", cls: "bg-rose-50 text-rose-700 border-rose-100" },
};

export function SiteScoreSection() {
  return (
    <section id="score" className="border-t border-slate-100 bg-white py-16 lg:py-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid items-start gap-10 lg:grid-cols-2 lg:gap-12 xl:gap-16">
          <div className="order-2 lg:order-1">
            <ScoreCard />
          </div>

          <div className="order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 bg-violet-50 border border-violet-100 rounded-full px-3.5 py-1.5 mb-7">
              <Star size={12} className="text-violet-600" fill="currentColor" />
              <span className="text-violet-700 uppercase tracking-widest" style={{ fontSize: "0.68rem", fontWeight: 700 }}>
                Diferencial Proprietário
              </span>
            </div>

            <h2
              className="mb-5 text-slate-900 text-3xl font-extrabold leading-tight tracking-tight md:text-4xl lg:text-[2.5rem]"
            >
              Painel de Autoridade — Excelência Individual com{" "}
              <span className="text-blue-600">Indicadores Transparentes.</span>
            </h2>

            <p className="mb-6 text-base leading-relaxed text-slate-600 md:text-lg">
              Substitua dezenas de relatórios complexos por um indicador definitivo. O DataFlow utiliza um
              cálculo proprietário com medidas e pesos específicos para gerar um{" "}
              <strong className="text-slate-800 font-semibold">Score de Aderência em tempo real</strong> e
              um programa meritocrático de evolução por desempenho.
            </p>

            <p className="mb-8 text-base leading-relaxed text-slate-600 md:text-lg">
              Saiba instantaneamente o nível de entrega, pontualidade, disciplina em rotinas, projetos
              liderados e colaboração de cada profissional — medindo excelência individual centrada em
              pessoas, não apenas em processos.
            </p>

            {/* Feature bullets */}
            <div className="grid auto-rows-fr gap-3 sm:grid-cols-2">
              {[
                { icon: TrendingUp, label: "Score individual e por equipe em tempo real" },
                { icon: Users, label: "Níveis de maturidade progressivos por resultado" },
                { icon: Clock, label: "Disciplina, projetos liderados e colaboração" },
                { icon: Star, label: "Ranking meritocrático por cargo e área" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex h-full min-h-[4.5rem] items-start gap-3 rounded-lg border border-slate-100 bg-slate-50 p-3.5">
                  <div className="p-1.5 bg-blue-50 rounded-md shrink-0">
                    <Icon size={14} className="text-blue-600" />
                  </div>
                  <span className="text-slate-700 leading-snug" style={{ fontSize: "0.835rem", fontWeight: 500 }}>
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Score Card Visualization ────────────────────────── */
function ScoreCard() {
  return (
    <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-6 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-slate-500 mb-0.5" style={{ fontSize: "0.72rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Score DataFlow
          </p>
          <p className="text-lg font-extrabold text-slate-900 md:text-xl">
            Painel de Performance · Junho 2024
          </p>
        </div>
        <div className="text-right">
          <p className="text-emerald-600" style={{ fontSize: "2.5rem", fontWeight: 900, lineHeight: 1, letterSpacing: "-0.03em" }}>
            94.1
          </p>
          <p className="text-emerald-600" style={{ fontSize: "0.68rem", fontWeight: 700 }}>↑ +3.2 pts vs. mês anterior</p>
        </div>
      </div>

      {/* Weight breakdown */}
      <div className="mb-6">
        <p className="text-slate-500 mb-3 uppercase tracking-widest" style={{ fontSize: "0.65rem", fontWeight: 600 }}>
          Composição do Score
        </p>
        <div className="flex flex-col gap-3">
          {scoreMetrics.map((m) => (
            <div key={m.label}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-slate-700" style={{ fontSize: "0.78rem", fontWeight: 500 }}>{m.label}</span>
                <div className="flex items-center gap-2">
                  <span className="text-slate-400" style={{ fontSize: "0.68rem" }}>Peso {m.weight}</span>
                  <span className="text-slate-900" style={{ fontWeight: 700, fontSize: "0.82rem" }}>{m.value}%</span>
                </div>
              </div>
              <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${m.value}%`, backgroundColor: m.color }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Team table */}
      <div>
        <p className="text-slate-500 mb-3 uppercase tracking-widest" style={{ fontSize: "0.65rem", fontWeight: 600 }}>
          Ranking da Equipe
        </p>
        <div className="bg-white rounded-xl border border-slate-200/80 overflow-hidden">
          {/* Table header */}
          <div className="grid grid-cols-[1fr_auto_auto] gap-4 px-4 py-2.5 border-b border-slate-100 bg-slate-50">
            <span className="text-slate-500" style={{ fontSize: "0.65rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Colaborador</span>
            <span className="text-slate-500 text-right" style={{ fontSize: "0.65rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Score</span>
            <span className="text-slate-500 text-right" style={{ fontSize: "0.65rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Nível</span>
          </div>
          {teamRows.map((row, i) => (
            <div
              key={row.name}
              className={`grid grid-cols-[1fr_auto_auto] gap-4 px-4 py-3 items-center ${i < teamRows.length - 1 ? "border-b border-slate-100" : ""}`}
            >
              <div>
                <p className="text-slate-900" style={{ fontSize: "0.78rem", fontWeight: 600 }}>{row.name}</p>
                <p className="text-slate-500" style={{ fontSize: "0.67rem" }}>{row.role}</p>
              </div>
              <div className="text-right">
                <span className="text-slate-900" style={{ fontWeight: 800, fontSize: "0.9rem" }}>{row.score}</span>
                <span
                  className={`ml-1 ${row.delta.startsWith("+") ? "text-emerald-600" : "text-rose-500"}`}
                  style={{ fontSize: "0.65rem", fontWeight: 700 }}
                >
                  {row.delta}
                </span>
              </div>
              <div>
                <span
                  className={`border rounded-md px-2 py-0.5 ${statusLabel[row.status].cls}`}
                  style={{ fontSize: "0.65rem", fontWeight: 700, whiteSpace: "nowrap" }}
                >
                  {statusLabel[row.status].label}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
